'use client';

import { configKeepDataInfiniy } from '@/config/useQuery';
import { cn, nameof } from '@/lib/utils';
import { AdministrativeUnitResponse } from '@/server/api/v1/administrative-unit/type';
import { LogicalOperator, OperatorType } from '@/server/base/base-trpc-service';
import { api } from '@/trpc/client';
import { AdministrativeUnitType } from '@/types/enum';
import { genericMemo } from '@/types/use';
import { useTranslations } from 'next-intl';
import {
    forwardRef,
    PropsWithChildren,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { ComboboxProps } from '../custom-select';
import { Textarea, TextareaProps } from '../ui/textarea';
import { AdministrativeUnitLevel } from './constants';
import { SelectUnit } from './select-unit';

export type AdministrativeUnitValues = {
    provinceCode?: string;
    province?: string;
    communeCode?: string;
    commune?: string;
    detailAddr?: string;
};

export type AdministrativeUnitValueOptions = {
    province?: AdministrativeUnitResponse;
    ward?: AdministrativeUnitResponse;
};

export type AdministrativeUnitProps = PropsWithChildren<{
    /**
     * Layout của component
     */
    layout?: 'vertical' | 'horizontal';

    values?: AdministrativeUnitValues;

    onChange?: (
        values?: AdministrativeUnitValues,
        valueOptions?: AdministrativeUnitValueOptions
    ) => void;

    /**
     * className của div bộc 3 select
     */
    wrapperClassName?: string;
    /**
     * className của từng item gồm label và select
     */
    itemClassName?: string;

    /**Classname của tag label */
    labelClassName?: string;

    /**Chỉ xem */
    readOnly?: boolean;

    /**Chặn việc gọi API  */
    disabledApi?: boolean;

    /**
     * props cua select
     */
    selectUnitProps?: ComboboxProps;

    /**
     * Hiển thị thêm input chi tiết địa chỉ khi chọn đơn vị hành chính (ví dụ: số nhà, tên đường, thôn xóm,...)
     */
    showInputDetailAddress?: boolean;
    /**
     * inputProps
     */
    textareaDetailProps?: TextareaProps;
}>;

export type AdministarativeUnitRefs = {
    onClear: () => void;
    onGetValue: () => AdministrativeUnitValues;
};

/**
 *
 * @param layout
 * @returns
 */
function getClassNameByLayout(layout: AdministrativeUnitProps['layout']) {
    return layout === 'vertical' ? 'flex flex-col' : 'flex';
}

/**
 * Chọn đơn vị hành chính:
 * * Tỉnh/Thành phố
 * * Quận/Huyện
 * * Phường/Xã
 */
const AdministrativeUnitLevel2 = forwardRef<
    AdministarativeUnitRefs,
    AdministrativeUnitProps
>((props, ref) => {
    const {
        layout = 'horizontal',
        values = {},
        onChange,
        labelClassName,
        itemClassName,
        wrapperClassName,
        readOnly = false,
        disabledApi = false,
        selectUnitProps,
        showInputDetailAddress = false,
        textareaDetailProps,
    } = props;

    /**
     * Đã load khởi tạo rồi
     */
    const _isInitLoaded = useRef(false);
    const apiUtils = api.useUtils();
    const transDef = useTranslations('System');

    const [objValues, setObjValues] = useState<AdministrativeUnitValueOptions>(
        {}
    );
    const [detailAddr, setDetailAddr] = useState<string>(
        values.detailAddr || ''
    );

    /**Đối với các địa chỉ không có thông tin chọn, phải load mặc định lên */
    const onLoadDefValue = useCallback(
        async (codes: string[]) => {
            if (codes.length) {
                const result = await apiUtils.administrativeUnit.Search.fetch(
                    {
                        filters: [
                            {
                                key: nameof<AdministrativeUnitResponse>(
                                    'Origin_Def_Code'
                                ),
                                values: codes,
                                logicalOperator: LogicalOperator.Or,
                                operatorType: OperatorType.In,
                            },
                        ],
                    },
                    {
                        ...configKeepDataInfiniy,
                    }
                );

                if (result.isSuccessStatus && result.data?.Items) {
                    const obj = result.data.Items.reduce((acc, item) => {
                        if (item.Type === AdministrativeUnitType.Province) {
                            acc.province = item;
                        } else if (item.Type === AdministrativeUnitType.Ward) {
                            acc.ward = item;
                        }
                        return acc;
                    }, {} as AdministrativeUnitValueOptions);

                    setObjValues(obj);
                }
                _isInitLoaded.current = true;
            }
        },
        [apiUtils.administrativeUnit.Search]
    );

    /**
     * Hàm khởi tạo data nếu cần
     */
    const onCheckDataWhenInitial = useCallback(async () => {
        const unit_codes = [values.provinceCode, values.communeCode];

        const isAtLeastOneValue = unit_codes.some((unit) => !!unit);

        if (isAtLeastOneValue) {
            if (!objValues.province && !objValues.ward) {
                const codes = [values.provinceCode, values.communeCode].filter(
                    (x) => !!x
                ) as string[];

                onLoadDefValue(codes);
            }
        }
    }, [objValues, onLoadDefValue, values]);

    useEffect(() => {
        if (_isInitLoaded.current) return;
        onCheckDataWhenInitial();
    }, [onCheckDataWhenInitial]);

    const onChangeValue = useCallback(
        (
            level: AdministrativeUnitLevel | 'd',
            value?: AdministrativeUnitResponse
        ) => {
            // Tính newValues từ objValues hiện tại, không dựa vào side-effect bên trong
            // updater của setState (updater có thể không chạy đồng bộ trước khi onChange gọi).
            const newValues: AdministrativeUnitValueOptions =
                level === AdministrativeUnitLevel.province
                    ? { province: value }
                    : level === AdministrativeUnitLevel.commnue
                      ? { ...objValues, ward: value }
                      : objValues;

            setObjValues(newValues);

            const { province, ward } = newValues;

            onChange?.(
                {
                    provinceCode: province?.Origin_Def_Code,
                    province: province?.Name,
                    communeCode: ward?.Origin_Def_Code,
                    commune: ward?.Name,
                    detailAddr: detailAddr,
                },
                newValues
            );
        },
        [onChange, detailAddr, objValues]
    );

    useImperativeHandle(ref, () => {
        return {
            onClear() {
                setObjValues({});
            },
            onGetValue() {
                const { province, ward } = objValues;

                return {
                    provinceCode: province?.Origin_Def_Code,
                    province: province?.Name,
                    wardCode: ward?.Origin_Def_Code,
                    ward: ward?.Name,
                    detailAddr: detailAddr,
                };
            },
        };
    });

    const { province } = objValues;

    return (
        <div
            id='Administrative_Unit'
            className={cn(
                getClassNameByLayout(layout),
                'mb-3 gap-3 text-sm',
                wrapperClassName
            )}
        >
            <div
                className={cn(
                    'flex items-center gap-x-2 max-xl:flex-col max-xl:gap-y-1',
                    itemClassName
                )}
            >
                <label
                    className={cn(
                        'font-medium max-xl:w-full xl:mr-2',
                        labelClassName
                    )}
                    htmlFor='province'
                >
                    <span className='leading-6 xl:leading-10'>
                        {transDef('System_Province')}:
                    </span>
                </label>
                <SelectUnit
                    level={AdministrativeUnitLevel.province}
                    value={values.provinceCode}
                    labelName={values.province}
                    onSelect={(value) =>
                        onChangeValue(AdministrativeUnitLevel.province, value)
                    }
                    readOnly={readOnly}
                    disabledApi={disabledApi}
                    {...selectUnitProps}
                />
            </div>
            <div
                className={cn(
                    'flex items-center gap-x-2 max-xl:flex-col max-xl:gap-y-1',
                    itemClassName
                )}
            >
                <label
                    className={cn(
                        'font-medium max-xl:w-full xl:mr-2',
                        labelClassName
                    )}
                    htmlFor='province'
                >
                    <span className='leading-6 xl:leading-10'>
                        {transDef('System_Ward')}:
                    </span>
                </label>
                <SelectUnit
                    level={AdministrativeUnitLevel.commnue}
                    parentId={province?.ID ?? undefined}
                    value={values.communeCode}
                    labelName={values.commune}
                    onSelect={(value) =>
                        onChangeValue(AdministrativeUnitLevel.commnue, value)
                    }
                    readOnly={readOnly}
                    disabledApi={disabledApi}
                    {...selectUnitProps}
                />
            </div>

            {showInputDetailAddress && (
                <div
                    className={cn(
                        'mt-0.5 flex gap-x-2 max-xl:flex-col max-xl:gap-y-1',
                        itemClassName
                    )}
                >
                    <label
                        className={cn(
                            'mt-1 font-medium max-xl:w-full xl:mr-2',
                            labelClassName
                        )}
                        htmlFor='detail-addr'
                    >
                        <span className=''>
                            {transDef('System_StreetAddress')}:
                        </span>
                    </label>
                    <Textarea
                        rows={3}
                        placeholder={transDef('System_Enter') + '. . .'}
                        {...textareaDetailProps}
                        readOnly={readOnly}
                        className={cn(
                            textareaDetailProps?.className,
                            'no-ring'
                        )}
                        value={detailAddr || values.detailAddr}
                        onChange={(e) => {
                            const address = e.target.value;
                            setDetailAddr(address);
                            onChange?.(
                                {
                                    ...values,
                                    detailAddr: address,
                                },
                                objValues
                            );
                        }}
                    />
                </div>
            )}
        </div>
    );
});

AdministrativeUnitLevel2.displayName = 'AdministrativeUnit';
export default genericMemo(AdministrativeUnitLevel2);
